import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
    children: ReactNode;
}

interface State {
    error: Error | null;
}

// Top-level crash guard. Without this, any uncaught exception during render
// anywhere in the tree unmounts the whole app to a blank page - the class
// component API (getDerivedStateFromError/componentDidCatch) is the only way
// React supports catching that; there's no hook equivalent. Renders the
// actual error message instead of nothing, so a crash is at least visible
// and diagnosable rather than looking like the app just disappeared.
export class ErrorBoundary extends Component<Props, State> {
    state: State = { error: null };

    static getDerivedStateFromError(error: Error): State {
        return { error };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        console.error('Unhandled error in component tree:', error, info.componentStack);
    }

    render() {
        if (this.state.error) {
            return (
                <div
                    style={{
                        minHeight: '100vh',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '12px',
                        padding: '24px',
                        background: 'var(--color-canvas, #1a1a1a)',
                        color: 'var(--color-text, #eee)',
                        fontFamily: 'var(--font-body, sans-serif)',
                        textAlign: 'center',
                    }}
                >
                    <h1 style={{ fontSize: '18px', margin: 0 }}>Something went wrong</h1>
                    <pre
                        style={{
                            maxWidth: '640px',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            fontSize: '13px',
                            opacity: 0.75,
                            margin: 0,
                        }}
                    >
                        {this.state.error.message}
                    </pre>
                    <button
                        onClick={() => window.location.reload()}
                        style={{
                            marginTop: '8px',
                            border: 'none',
                            background: 'var(--color-primary, #4a90d9)',
                            color: '#fff',
                            fontFamily: 'inherit',
                            fontSize: '13px',
                            fontWeight: 600,
                            padding: '9px 18px',
                            borderRadius: '999px',
                            cursor: 'pointer',
                        }}
                    >
                        Reload
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}
